import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { timeToMinutes } from './TTWeekView';

// ── Constants ────────────────────────────────────────────────
const HOUR_HEIGHT = 60;
const TOTAL_HOURS = 24;
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
const HOURS       = Array.from({ length: TOTAL_HOURS }, (_, i) => i);
const SCROLL_TO   = 7;

// ── Absence config ───────────────────────────────────────────
const ABSENCE_COLORS = {
  VACATION: '#0D9488', SICK: '#F59E0B', COMPENSATION: '#3B82F6',
  MILITARY: '#64748B', CIVIL_SERVICE: '#6366F1',
  MATERNITY: '#8B5CF6', PATERNITY: '#8B5CF6', PARENTAL: '#8B5CF6',
  BEREAVEMENT: '#475569', MARRIAGE: '#EC4899',
  UNPAID: '#94A3B8', OTHER: '#94A3B8',
};
const ABSENCE_LABELS = {
  VACATION: 'Vacation', SICK: 'Sick Leave', COMPENSATION: 'Compensation',
  MILITARY: 'Military Service', CIVIL_SERVICE: 'Civil Service',
  MATERNITY: 'Maternity Leave', PATERNITY: 'Paternity Leave', PARENTAL: 'Parental Leave',
  BEREAVEMENT: 'Bereavement', MARRIAGE: 'Marriage Leave',
  UNPAID: 'Unpaid Leave', OTHER: 'Other Absence',
};

// ── Helpers ──────────────────────────────────────────────────
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function absenceCoversDay(a, day) {
  const ds = toDateStr(day);
  return ds >= a.startDate && ds <= a.endDate;
}

function fmtEntryDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function minutesToTime(total) {
  const clamped = Math.max(0, Math.min(Math.round(total), GRID_HEIGHT - 1));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function snapTo15(min) { return Math.round(min / 15) * 15; }

function fmtDisplay(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  return `${h}:${String(m).padStart(2, '0')}`;
}

function getCurrentMinutes() {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

// ── Current time line ────────────────────────────────────────
function CurrentTimeLine() {
  const [min, setMin] = useState(getCurrentMinutes);
  useEffect(() => {
    const t = setInterval(() => setMin(getCurrentMinutes()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <Box sx={{
      position: 'absolute', top: min, left: 0, right: 0,
      zIndex: 6, pointerEvents: 'none', display: 'flex', alignItems: 'center',
    }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#EF4444', flexShrink: 0, ml: '-5px' }} />
      <Box sx={{ flex: 1, height: 2, bgcolor: '#EF4444' }} />
    </Box>
  );
}

// ── Timed entry block ────────────────────────────────────────
function EntryBlock({ entry, projectColor, onEdit, onDelete, onUpdate, colRef }) {
  const color      = projectColor(entry.projectId);
  const localRef   = useRef(entry);
  const hasDragged = useRef(false);
  const [local, setLocal] = useState(entry);

  useEffect(() => { localRef.current = entry; setLocal(entry); }, [entry]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    hasDragged.current = false;

    const duration      = timeToMinutes(localRef.current.endTime) - timeToMinutes(localRef.current.startTime);
    const rect0         = colRef.current.getBoundingClientRect();
    const offsetInBlock = e.clientY - rect0.top - timeToMinutes(localRef.current.startTime);

    const onMove = (e) => {
      hasDragged.current = true;
      const rect     = colRef.current.getBoundingClientRect();
      const rawTop   = e.clientY - rect.top - offsetInBlock;
      const newStart = snapTo15(Math.max(0, Math.min(rawTop, GRID_HEIGHT - duration)));
      const updated  = {
        ...localRef.current,
        startTime:    minutesToTime(newStart),
        endTime:      minutesToTime(newStart + duration),
        workingHours: duration / 60,
      };
      localRef.current = updated;
      setLocal({ ...updated });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (hasDragged.current) onUpdate({ ...localRef.current });
      else onEdit(entry);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colRef, entry, onEdit, onUpdate]);

  const handleResizeDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY   = e.clientY;
    const origEnd  = timeToMinutes(localRef.current.endTime);
    const minStart = timeToMinutes(localRef.current.startTime);

    const onMove = (e) => {
      const newEnd  = snapTo15(Math.max(minStart + 15, Math.min(origEnd + (e.clientY - startY), GRID_HEIGHT)));
      const updated = { ...localRef.current, endTime: minutesToTime(newEnd), workingHours: (newEnd - minStart) / 60 };
      localRef.current = updated;
      setLocal({ ...updated });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      onUpdate({ ...localRef.current });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [onUpdate]);

  const startMin = timeToMinutes(local.startTime);
  const endMin   = timeToMinutes(local.endTime);
  if (startMin === null || endMin === null) return null;

  const top      = startMin;
  const height   = Math.max(endMin - startMin, 24);
  const showTime = height >= 40;
  const showDesc = height >= 72 && entry.description;

  return (
    <Box onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        top: `${top}px`, left: '6px', right: '6px',
        height: `${height}px`,
        bgcolor: `${color}18`, border: `1px solid ${color}45`, borderLeft: `4px solid ${color}`,
        borderRadius: '6px', cursor: 'grab', overflow: 'hidden', zIndex: 3,
        '&:active': { cursor: 'grabbing', zIndex: 10 },
        '&:hover':  { boxShadow: `0 3px 14px ${color}28`, borderColor: `${color}75` },
        '&:hover .ea': { opacity: 1 },
        '& .ea': { opacity: 0, transition: 'opacity 0.12s' },
        userSelect: 'none',
        transition: 'box-shadow 0.12s',
      }}
    >
      <Box sx={{ px: 1.25, pt: 0.75, overflow: 'hidden', height: 'calc(100% - 12px)' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color, lineHeight: 1.3 }}>
          {entry.projectName}
        </Typography>
        {showTime && (
          <Typography sx={{ fontSize: 11, color: `${color}CC`, mt: 0.25 }}>
            {fmtDisplay(local.startTime)} – {fmtDisplay(local.endTime)}
            {' · '}
            {local.workingHours != null ? (local.workingHours % 1 === 0 ? local.workingHours : local.workingHours.toFixed(1)) : ''}h
          </Typography>
        )}
        {showDesc && (
          <Typography sx={{ fontSize: 11, color: '#64748B', mt: 0.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {entry.description}
          </Typography>
        )}
      </Box>

      <Box className="ea" sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25 }}>
        <IconButton size="small" onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
          sx={{ p: '3px', color: '#94A3B8', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: '4px', '&:hover': { color: '#427898' } }}>
          <EditIcon sx={{ fontSize: 12 }} />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(entry); }}
          sx={{ p: '3px', color: '#94A3B8', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: '4px', '&:hover': { color: '#EF4444' } }}>
          <DeleteIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Box>

      <Box onMouseDown={handleResizeDown} onClick={(e) => e.stopPropagation()}
        sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 12, cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {height > 36 && (
          <Box sx={{ width: 24, height: 2.5, bgcolor: color, borderRadius: 2, opacity: 0.35 }} />
        )}
      </Box>
    </Box>
  );
}

// ── Main day view ────────────────────────────────────────────
export default function TTDayView({ currentDate, entries, absences = [], projectColor, onAddDay, onEdit, onDelete, onUpdate }) {
  const colRef    = useRef(null);
  const scrollRef = useRef(null);
  const today     = new Date();
  const isToday   = isSameDay(currentDate, today);

  const dayEntries  = entries.filter(e => {
    const ed = e.entryDate ? fmtEntryDate(e.entryDate) : null;
    return ed && isSameDay(ed, currentDate);
  });

  const dayAbsences = absences.filter(a => absenceCoversDay(a, currentDate) && a.status === 'APPROVED');
  const timed       = dayEntries.filter(e => e.startTime && e.endTime);
  const unscheduled = dayEntries.filter(e => !e.startTime || !e.endTime);
  const totalHours  = dayEntries.reduce((s, e) => s + (e.workingHours || 0), 0);
  const billableH   = dayEntries.filter(e => e.billable !== false).reduce((s, e) => s + (e.workingHours || 0), 0);
  const label       = currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = SCROLL_TO * HOUR_HEIGHT;
  }, [currentDate]);

  const handleSlotClick = (hour) => {
    const st = `${String(hour).padStart(2, '0')}:00`;
    onAddDay(currentDate, st);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── Day banner ── */}
      <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 3, boxShadow: '0 1px 4px rgba(15,23,42,0.04)' }}>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.3 }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: 32, fontWeight: 800, color: isToday ? '#427898' : '#0F172A', lineHeight: 1 }}>
              {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)}h
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>logged</Typography>
          </Box>
        </Box>
        {totalHours > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, pl: 2.5, ml: 1, borderLeft: '1px solid #EEF2F6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#16A34A' }} />
              <Typography sx={{ fontSize: 12, color: '#475569' }}>
                {billableH % 1 === 0 ? billableH : billableH.toFixed(1)}h billable
              </Typography>
            </Box>
            {billableH < totalHours && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#CBD5E1' }} />
                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                  {(totalHours - billableH) % 1 === 0 ? (totalHours - billableH) : (totalHours - billableH).toFixed(1)}h internal
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Add entry for this day">
          <Box onClick={() => onAddDay(currentDate)} sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
            bgcolor: '#427898', borderRadius: 1.5, cursor: 'pointer',
            '&:hover': { bgcolor: '#355f77' }, transition: 'background-color 0.15s',
          }}>
            <AddIcon sx={{ fontSize: 14, color: '#fff' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>Add entry</Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* ── Absence banners ── */}
      {dayAbsences.map(a => {
        const color = ABSENCE_COLORS[a.type] || '#94A3B8';
        const name  = `${a.employeeFirstName ?? ''} ${a.employeeLastName ?? ''}`.trim();
        return (
          <Box key={a.id} sx={{
            px: 2, py: 1, bgcolor: `${color}10`, borderRadius: 2,
            border: `1px solid ${color}30`, borderLeft: `4px solid ${color}`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
                {ABSENCE_LABELS[a.type] || a.type}{a.halfDay ? ` — ${a.halfDayPeriod === 'MORNING' ? 'Morning' : 'Afternoon'}` : ''}
              </Typography>
              {name && (
                <Typography sx={{ fontSize: 11, color: '#64748B' }}>{name}</Typography>
              )}
            </Box>
          </Box>
        );
      })}

      {/* ── Unscheduled entries ── */}
      {unscheduled.length > 0 && (
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #EEF2F6', p: 1.5, boxShadow: '0 1px 4px rgba(15,23,42,0.04)' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Unscheduled
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {unscheduled.map(e => {
              const color = projectColor(e.projectId);
              return (
                <Box key={e.id} onClick={() => onEdit(e)} sx={{
                  px: 1.25, py: 0.75, borderRadius: 1.5,
                  border: '1px solid #EEF2F6', borderLeft: `3px solid ${color}`,
                  cursor: 'pointer', bgcolor: `${color}08`,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  '&:hover': { bgcolor: `${color}14`, borderColor: `${color}50` },
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
                      {e.projectName}
                    </Typography>
                    {e.description && (
                      <Typography sx={{ fontSize: 11, color: '#64748B', mt: 0.1 }}>{e.description}</Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                    {e.workingHours != null ? (e.workingHours % 1 === 0 ? e.workingHours : e.workingHours.toFixed(1)) : '—'}h
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Time grid ── */}
      <Box sx={{ display: 'flex', border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.04)' }}>

        {/* Time gutter */}
        <Box sx={{ width: 56, flexShrink: 0, borderRight: '1px solid #EEF2F6', bgcolor: '#FAFBFC', userSelect: 'none' }}>
          <Box sx={{ height: 0 }} /> {/* top spacer aligns with grid */}
          {HOURS.map(h => (
            <Box key={h} sx={{ height: HOUR_HEIGHT, position: 'relative' }}>
              {h > 0 && (
                <Typography sx={{ position: 'absolute', top: -8, right: 10, fontSize: 10, fontWeight: 600, color: '#B0BEC5', lineHeight: 1 }}>
                  {String(h).padStart(2, '0')}:00
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Main column (scrollable) */}
        <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', maxHeight: '70vh', minHeight: 300 }}>
          <Box ref={colRef} sx={{ position: 'relative', height: GRID_HEIGHT, bgcolor: isToday ? '#FAFEFF' : '#fff' }}>

            {/* Absence overlays (behind everything) */}
            {dayAbsences.map(a => {
              const color  = ABSENCE_COLORS[a.type] || '#94A3B8';
              const top    = a.halfDay && a.halfDayPeriod === 'AFTERNOON' ? GRID_HEIGHT / 2 : 0;
              const height = a.halfDay ? GRID_HEIGHT / 2 : GRID_HEIGHT;
              return (
                <Box key={a.id} sx={{
                  position: 'absolute', top, left: 0, right: 0, height,
                  bgcolor: `${color}10`, borderLeft: `3px solid ${color}35`,
                  zIndex: 1, pointerEvents: 'none',
                }} />
              );
            })}

            {/* Hour grid lines */}
            {HOURS.map(h => (
              <Box key={h} onClick={() => handleSlotClick(h)} sx={{
                position: 'absolute', top: h * HOUR_HEIGHT, left: 0, right: 0,
                height: HOUR_HEIGHT,
                borderTop: h === 0 ? 'none' : `1px solid ${h % 3 === 0 ? '#E8ECF0' : '#F3F5F8'}`,
                cursor: 'crosshair',
                '&:hover': { bgcolor: '#EFF6FF20' },
              }} />
            ))}

            {/* Today indicator */}
            {isToday && <CurrentTimeLine />}

            {/* Entry blocks */}
            {timed.map(e => (
              <EntryBlock key={e.id} entry={e} projectColor={projectColor}
                onEdit={onEdit} onDelete={onDelete} onUpdate={onUpdate} colRef={colRef} />
            ))}

            {/* Click-to-add hint when empty */}
            {timed.length === 0 && (
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none',
              }}>
                <Typography sx={{ fontSize: 13, color: '#E2E8F0' }}>
                  Click a time slot to add an entry
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
